/**
 * PartnerReferrals — обезличенный список приведённых клиентов. Шаг Flow (step="partner_referrals").
 *
 * По требованию приватности партнёр не видит персональных данных: только короткий внутренний
 * идентификатор, дату привлечения, статус проверки, оборот и начисленное. Бэкенд других полей
 * не отдаёт — это не скрытие на фронте.
 *
 * Пропсы: onBack — вернуться в кабинет.
 */
import { useEffect, useState } from "react";
import { Screen, Card, Row } from "@/components/Screen";
import { BackLink } from "@/components/BackLink";
import { Note } from "@/components/Note";
import { Spinner } from "@/components/Spinner";
import { partnerApi, type PartnerReferral, type PartnerSubAgent } from "@/lib/api";

const KYC_LABEL: Record<PartnerReferral["kyc"], string> = {
  verified: "Проверен",
  pending: "На проверке",
  rejected: "Отказ",
  none: "Без проверки",
};

export function PartnerReferrals({ onBack }: { onBack: () => void }) {
  const [clients, setClients] = useState<PartnerReferral[] | null>(null);
  const [subAgents, setSubAgents] = useState<PartnerSubAgent[]>([]);

  useEffect(() => {
    partnerApi
      .referrals()
      .then((r) => {
        setClients(r.clients);
        setSubAgents(r.sub_agents);
      })
      .catch(() => setClients([]));
  }, []);

  return (
    <Screen title="Мои рефералы" subtitle="Данные клиентов обезличены">
      <BackLink onClick={onBack} className="mb-4">← Назад</BackLink>

      {clients === null && (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      )}

      {clients?.length === 0 && (
        <Note>
          Пока никого. Поделитесь ссылкой или кодом — как только по ним придёт клиент, он появится
          здесь.
        </Note>
      )}

      {clients?.map((c) => (
        <Card key={c.id} className="mb-2.5">
          <div className="flex items-center justify-between">
            <span className="font-mono text-[15px]">{c.id}</span>
            <span className="text-[12px] text-white/40">
              {c.attached_at ? new Date(c.attached_at).toLocaleDateString("ru-RU") : "—"}
            </span>
          </div>
          <div className="mt-2 border-t border-white/[0.06] pt-1">
            <Row label="Проверка" value={KYC_LABEL[c.kyc]} />
            <Row label="Оборот" value={`${c.turnover_rub.toLocaleString("ru-RU")} ₽`} />
            <Row label="Начислено" value={`${c.earned_usd.toFixed(2)} $`} />
          </div>
        </Card>
      ))}

      {subAgents.length > 0 && (
        <>
          <h2 className="mb-2 mt-6 text-[15px] font-semibold">Второй уровень</h2>
          <p className="mb-3 text-[13px] leading-snug text-white/45">
            Партнёры, которых привели вы. Здесь их оборот и ваша доля с него.
          </p>
          {subAgents.map((s) => (
            <Card key={s.id} className="mb-2.5">
              <div className="font-mono text-[15px]">{s.id}</div>
              <div className="mt-2 border-t border-white/[0.06] pt-1">
                <Row label="Оборот их клиентов" value={`${s.turnover_rub.toLocaleString("ru-RU")} ₽`} />
                <Row label="Начислено вам" value={`${s.earned_usd.toFixed(2)} $`} />
              </div>
            </Card>
          ))}
        </>
      )}
    </Screen>
  );
}
